import { PanelDefaultRoute, SuperAdminDefaultRoute } from "@src/router/routes";

import { DATE_FORMAT_API } from "../constants";
import { STATUS_BADGE } from "../constants/panel/common";
import capitalize from "lodash/capitalize";
// ** UseJWT import to get config
import { confirmAlert } from "./toast";
import dayjs from "dayjs";
import each from "lodash/each";
import join from "lodash/join";
import sample from "lodash/sample";
import size from "lodash/size";
import split from "lodash/split";
import themeConfig from "@configs/theme-config";
import times from "lodash/times";
import tldMap from "../constants/email-tld-list";
// import  secureLocalStorage  from  "react-secure-storage";

/**
 * Store Management
 * @param key
 * @param store
 */
export const getItemFromStore = (key, store = localStorage) => {
    try {
        const item = store.getItem(key);
        return JSON.parse(item);
    } catch (error) {
        console.error(`Error parsing item for key "${key}":`, error);
        return store.getItem(key); // fallback: return as-is
    }
};
export const setItemToStore = (key, payload, store = localStorage) => store.setItem(key, JSON.stringify(payload));
export const removeItemFromStore = (key, store = localStorage) => {
    return store.removeItem(key);
};

/**
 * It returns true if the current URL contains the string "/superadmin"
 * @returns A boolean value.
 */
export const isSuperAdminUrl = () => {
    return window.location.pathname.includes("/superadmin");
};

/**
 * This function retrieves the "loginViaSuperadmin" item from the store.
 * @returns The function `loginViaSuperadmin` is returning the value of the item retrieved from the
 * store with the key "loginViaSuperadmin".
 */
export const loginViaSuperadmin = () => {
    return getItemFromStore("loginViaSuperadmin");
};

/**
 ** This function is used for demo purpose route navigation
 ** In real app you won't need this function because your app will navigate to same route for each users regardless of ability
 ** Please note type field is just for showing purpose it's not used by anything in frontend
 ** We are checking type just for ease
 * ? NOTE: If you have different pages to navigate based on user ability then this function can be useful. However, you need to update it.
 * @param {String} userType Type of user
 **/
export const getHomeRouteForLoggedInUser = (userType) => {
    if (userType) {
        return PanelDefaultRoute;
    }
    if (userType === "superadmin" || isSuperAdminUrl()) return SuperAdminDefaultRoute;
    return "/login";
};

export const destroyUserSession = () => {
    sessionStorage.clear();
    localStorage.clear();
};

/**
 * It returns the ability object from the store if it exists, otherwise it returns the
 * superAdminAbility object from the store
 * @returns The ability of the user.
 */
export const getAbility = () => {
    return getItemFromStore("ability");
};

/**
 * It returns the value of the token stored in the browser's local storage, if it exists
 * @returns The token from the local storage.
 */
export const handleToken = (type = "panel") => {
    if (type === "superAdmin") {
        return getItemFromStore("token-superadmin");
    }
    return getItemFromStore("token");
};

/*
 * The numberCommaFormatter function takes a number as input and returns a string representation of the
 * number with commas separating the thousands.
 * @param number - The `number` parameter is the number that you want to format with commas.
 * @returns the input number formatted with commas.
 */
export const numberCommaFormatter = (number) => {
    let x = number;
    if (typeof number === "number") {
        x = x.toLocaleString();
    }
    return x || 0;
};

/**
 *  Statistics Formatter
 * @param num
 */
export const statFormatter = (num) => {
    num = num ? num.toString().replace(/[^0-9.]/g, "") : 0;
    if (num < 10000) {
        const typeCastNum = Number(num);
        if (Number.isInteger(typeCastNum)) {
            return num || 0;
        } else {
            return typeCastNum.toFixed(2);
        }
    }
    const si = [
        { v: 1e3, s: "K" },
        { v: 1e6, s: "M" },
        { v: 1e9, s: "B" },
        { v: 1e12, s: "T" },
        { v: 1e15, s: "P" },
        { v: 1e18, s: "E" }
    ];
    let index;
    for (index = size(si) - 1; index > 0; index--) {
        if (num >= si[index].v) {
            break;
        }
    }
    return (
        (num / si[index].v).toFixed(2).replace(/\.0+$|(\.[0-9]*[1-9])0+$/, "$1") +
    si[index].s
    );
};

/**
 * Function to extract initials from content
 * @param nameString
 */
export const getInitials = (nameString = "") => {
    let initials = "";
    nameString = nameString.replace(/\s+/g, " ");
    const fullName = nameString.trim().split(" ");
    if (size(fullName) === 1) {
        initials = fullName[0][0];
    } else {
        initials = fullName[0][0] + fullName[1][0];
    }
    return initials?.toUpperCase();
};

/**
 * It tries to fetch a file from Google's ad server, and if it fails, it returns true, otherwise it
 * returns false
 * @returns A promise.
 */
export const checkForAdBlocker = () => {
    function isAdBlockActive (callback) {
    // Create a test element
        const testElement = document.createElement("div");
        testElement.innerHTML = "&nbsp;";
        testElement.className = "ad";

        // Append the test element to the document
        document.body.appendChild(testElement);

        // Wait for a short period
        setTimeout(function () {
            // Check if the test element has been affected by an ad blocker
            const isBlocked = testElement.offsetHeight === 0;

            // Remove the test element from the document
            testElement.remove();

            // Execute the callback with the result
            callback(isBlocked);
        }, 100);
    }

    // Usage
    isAdBlockActive(function (result) {
        if (result) {
            confirmAlert({
                title            : "Caution! Disable Ad Block",
                text             : "Please disable ad blocker based plugins, else the panel will not work as expected.",
                icon             : "warning",
                showCancelButton : false,
                confirmButtonText: "Refresh",
                preConfirm       : () => {
                    window.location.reload();
                }
            });
        }
    });
};

/**
 * If the current date is between December 20th and January 6th, return true.
 * @returns A boolean value.
 */
export const checkChristmasDate = () => {
    const start = `${
        dayjs().format("MM") === "01" ? dayjs().subtract(1, "year").format("YYYY") : dayjs().format("YYYY")
    }-12-20`;
    const end = `${
        dayjs().format("MM") === "01" ? dayjs().format("YYYY") : dayjs().add(1, "year").format("YYYY")
    }-01-06`;
    const date = dayjs().format(DATE_FORMAT_API);
    return date >= start && date <= end;
};

export const validateEmailTLD = (value) => {
    if (!value) {
        return true;
    }
    let isValidTLD = false;

    // Extract top level domain value from email
    const tldValue = value?.match(/([^.]+$)/);

    // Check if valid TLD
    if (tldValue && size(tldValue) && tldMap[tldValue[0]]) {
        isValidTLD = true;
    }

    return isValidTLD;
};

export const removeEmptyFields = (obj = {}) => {
    // If the input is not an object, return it
    if (typeof obj !== "object" || obj === null) {
        return obj;
    }
    // Recursively remove empty fields from nested objects
    for (const [key, value] of Object.entries(obj)) {
        obj[key] = removeEmptyFields(value);
        // Skip Date objects
        if (obj[key] instanceof Date) {
            continue;
        }
        // Trim string values
        if (typeof obj[key] === "string") {
            obj[key] = obj[key].trim();
        }

        if (obj[key] === undefined || obj[key] === null || obj[key] === "") {
            delete obj[key];
        }
        if (typeof obj[key] === "object" && size(Object.keys(obj[key])) === 0) {
            delete obj[key];
        }
    }
    return obj;
};

/**
 * to get badge for status as per status constants
 * @param {*} data
 * @returns color and title
 */
export function getStatusBadge (data) {
    let color = "primary";
    let title = "";
    if (data) {
        color = `${STATUS_BADGE[data]?.color || "primary"}`;
        title = STATUS_BADGE[data]?.title || data;
    }
    return { color, title };
}

/**
* React Select Theme Colors
@param theme
*/
export const selectThemeColors = (theme) => ({
    ...theme,
    colors: {
        ...theme.colors,
        primary25: "#7367f01a", // for option hover bg-color
        primary  : "#fb4a1c", // for selected option bg-color
        neutral10: "#fb4a1c", // for tags bg-color
        neutral20: "#ededed", // for input border-color
        neutral30: "#ededed" // for input hover border-color
    }
});

/**
 * It takes a string and returns a hash code.
 * @param str - The string to hash.
 * @returns A hash code for the string.
 */
export const hashCode = (str) => {
    let hash = 0;
    each(split(join(split(str, " "), ""), ""), (i) => {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    });
    return hash;
};
/**
 * It toggles the value of the menuCollapsed key in the localStorage and then dispatches an event to
 * the window object
 */
export function handleMenuCollapsed (value) {
    setItemToStore(
        "menuCollapsed",
        value === "false" ? true : !getItemFromStore("menuCollapsed")
    );
    window.dispatchEvent(new Event("storage"));
}
/**
 * It takes a string, hashes it, and returns a color
 * @param str - The string to hash.
 * @returns A string that is a color in hsla format.
 */
export const hashCodeToRgbaColor = (str) => {
    return `hsla(${~~(360 * hashCode(str))}, 99%, 86%)`;
};

/**
 * The function `getPlural` returns the plural form of a given text based on the length parameter.
 * @param text - The `text` parameter is a string that represents the singular form of a word.
 * @param length - The `length` parameter is used to determine whether the `text` should be pluralized
 * or not. If `length` is equal to 1, the `text` will not be pluralized. Otherwise, the `text` will be
 * appended with an "s" to make it plural.
 * @returns If the length is equal to 1, the function will return the original text. Otherwise, it will
 * return the text with an "s" appended to it.
 */
export const getPlural = ({ text, length, ies = false }) => {
    if (length <= 1) {
        return text;
    } else if (ies) {
        return `${text.slice(0, -1)}ies`;
    } else {
        return `${text}s`;
    }
};

/**
 * Parse URL as per provided values like string, number, boolean, object and array
 * @param obj
 */
export const parseUrl = (Obj = {}, key) => {
    const arrayUrl = [];

    const handleNestedObject = (obj, parentKey) => {
        return Object.keys(obj)
            .map((subKey) => {
                const fullKey = parentKey ? `${parentKey}[${subKey}]` : subKey;
                const value = obj[subKey];

                if (
                    typeof value === "object" &&
          !Array.isArray(value) &&
          value !== null
                ) {
                    // Recursive call for nested objects
                    return handleNestedObject(value, fullKey);
                } else if (Array.isArray(value)) {
                    // Handle array
                    return value
                        .map((item) => `${fullKey}[]=${encodeURIComponent(item)}`)
                        .join("&");
                } else {
                    // Handle primitive types
                    return `${fullKey}=${encodeURIComponent(value)}`;
                }
            })
            .join("&");
    };

    const trimSearchObject = Object.keys(Obj).reduce((acc, curr) => {
        const value = Obj[curr];
        if (value) {
            if (
                typeof value === "string" ||
        typeof value === "number" ||
        typeof value === "boolean"
            ) {
                acc[curr] = typeof value === "string" ? value.trim() : value;
            } else if (Array.isArray(value)) {
                const myArrayQry = value
                    .map((el) => `${curr}[]=${encodeURIComponent(el)}`)
                    .join("&");
                if (myArrayQry) {
                    arrayUrl.push(myArrayQry);
                }
            } else if (typeof value === "object" && value !== null) {
                if (key && curr === key) {
                    // Handle specific key for object
                    const nestedQuery = handleNestedObject({ [key]: value }, "");
                    if (nestedQuery) {
                        arrayUrl.push(nestedQuery);
                    }
                } else if (!key) {
                    // Handle all keys if no specific key is provided
                    const nestedQuery = handleNestedObject(value, curr);
                    if (nestedQuery) {
                        arrayUrl.push(nestedQuery);
                    }
                }
            }
        }
        return acc;
    }, {});

    const finalArrayUrl = arrayUrl.join("&");
    let convertUrl = new URLSearchParams(trimSearchObject).toString();
    if (convertUrl) {
        convertUrl = `${convertUrl}`;
    }
    return `${convertUrl}${finalArrayUrl ? `&${finalArrayUrl}` : ""}`;
};

/**
     * Function to convert date to relative format like '10 minutes ago', '1 hour ago', etc.

/**
 * It returns the page number to be used for the next request to the server
 * @returns - If action is delete, return page - 1 if page is not 1 and data.length is 1
 *     - If action is add, return newPage
 */
export const getValidPage = ({ pagination, data, action }) => {
    const { page, limit, total } = pagination;
    const tempTotal = total + 1;
    if (action === "delete") return {
        page: page !== 1 && size(data) === 1 ? page - 1 : page
    };
    if (action === "add") {
        const newPage =
      tempTotal % limit === 0 ? tempTotal / limit : Math.floor(tempTotal / limit) + 1;
        return {
            page: newPage
        };
    }
};

export function setDefaultTheme (color) {
    const defaultColor = themeConfig.app.primaryColor;
    const root = document.documentElement;
    if (color && color !== defaultColor) {
        root?.style.setProperty("--sidebar-color", color);
        root?.style.setProperty("--bs-primary", color);
        root?.style.setProperty("--primary-color", color);
        root?.style.setProperty("--bs-primary-rgb", color);
    }
}

export const formatTimestamp = (timestamp) => {
    if (
        !timestamp ||
    (typeof timestamp !== "string" && typeof timestamp !== "number")
    ) {
        return "Invalid timestamp";
    }

    let date;
    if (dayjs.isDayjs(timestamp)) {
        date = timestamp;
    } else if (typeof timestamp === "string") {
        date = dayjs(timestamp);
        if (!date.isValid()) {
            const numericTimestamp = parseInt(timestamp, 10);
            if (!isNaN(numericTimestamp)) {
                date = dayjs(numericTimestamp * 1000);
            }
        }
    } else {
        const isSeconds = timestamp < 10000000000;
        date = dayjs(isSeconds ? timestamp * 1000 : timestamp);
    }

    if (!date.isValid()) {
        return "Invalid timestamp";
    }

    const daysDiff = -Math.round(dayjs().diff(date, "day", true));
    return new Intl.RelativeTimeFormat("en", { numeric: "auto" }).format(
        daysDiff,
        "day"
    );
};
export const generateRandomString = (length = 10) => {
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
    return times(length, () => sample(characters)).join("");
};

/**
 * to decorate data for react-select
 */
export function decorateResponse (data = []) {
    const deco = data.map((item) => ({
        ...item,
        value : item._id || item,
        label : capitalize(item.name || item),
        image : item.image,
        status: item.status,
        icon  : item.icon
    }));
    return deco || [];
}